import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import AccountBasicsStep, { type AccountDraft } from "./activation/AccountBasicsStep";
import ArtistBasicsStep, { type ArtistDraft } from "./activation/ArtistBasicsStep";
import ComparablesStep from "./activation/ComparablesStep";
import GoalsStep from "./activation/GoalsStep";
import Loading from "@/components/Loading";
import { useToast } from "@/components/ToastProvider";
import type { OnboardingStatus, OnboardingStep, PrefillAccountInfo } from "@/types/onboarding";
import { cn } from "@/lib/utils";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";
import { setActiveArtistId } from "@/api/client";
import { useAuth } from "@/lib/authContext";
import { fetchCurrentAccount } from "@/api/account";
import { needsBillingContextRefresh, requiresBillingBeforeOnboarding } from "@/lib/billingGate";

/**
 * MVP first-run activation: Account → Artist → Goals → Comparables → Discover.
 * Email, calendar and organization setup are no longer onboarding steps; they
 * remain available later in Settings. Nothing here calls AI or external lookups.
 */
type ActivationStep = "account" | "artist" | "goals" | "comparables";

const STEP_ORDER: ActivationStep[] = ["account", "artist", "goals", "comparables"];
const STEP_LABELS: Record<ActivationStep, string> = {
  account: "Account",
  artist: "Artist",
  goals: "Goals",
  comparables: "Comparable artists",
};

/** Map any stored step (including legacy ones) onto the activation journey. */
export function toActivationStep(step: OnboardingStep | null | undefined): ActivationStep {
  switch (step) {
    case "artist":
    case "organization":
      return "artist";
    case "goals":
    case "email":
    case "calendar":
      return "goals";
    case "comparables":
      return "comparables";
    default:
      return "account";
  }
}

type OnboardingLocationState = {
  prefillAccount?: PrefillAccountInfo;
  /** Set when this step was reached by moving forward from ``from`` (enables history Back). */
  from?: ActivationStep;
};

/** Step shown in the URL (/signup/<step>), or null for legacy/unknown paths. */
function stepFromPath(pathname: string): ActivationStep | null {
  const segment = pathname.replace(/^\/signup\/?/, "").split("/")[0];
  return (STEP_ORDER as string[]).includes(segment) ? (segment as ActivationStep) : null;
}

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const [prefillAccount, setPrefillAccount] = useState<PrefillAccountInfo | null>(null);
  const { push } = useToast();
  const { status, loading, error, refresh: refreshStatus } = useOnboardingProgress();
  const { signout, profile, accessToken, completeLogin } = useAuth();
  // Set when the user finishes the last step in this session → land in Discover.
  const justCompletedRef = useRef(false);
  // Values typed on earlier steps survive Back/forward navigation (never passwords).
  const [accountDraft, setAccountDraft] = useState<AccountDraft | null>(null);
  const [artistDraft, setArtistDraft] = useState<ArtistDraft | null>(null);

  const queryPrefill = useMemo<PrefillAccountInfo | null>(() => {
    const params = new URLSearchParams(location.search);
    const pick = (keys: string[]) => {
      for (const key of keys) {
        const value = params.get(key);
        if (value) return value.trim();
      }
      return null;
    };
    const email = pick(["email", "signup_email", "prefill_email"]);
    const first_name = pick(["first_name", "firstName", "prefill_first_name"]);
    const last_name = pick(["last_name", "lastName", "prefill_last_name"]);
    if (!email && !first_name && !last_name) return null;
    return { email: email ?? undefined, first_name: first_name ?? undefined, last_name: last_name ?? undefined };
  }, [location.search]);

  useEffect(() => {
    if (!error) return;
    const statusCode = typeof (error as { status?: number })?.status === "number" ? (error as { status: number }).status : null;
    if (statusCode === 401 || statusCode === 404) {
      signout();
      push({ title: "Onboarding", message: "Session expired. Please sign up again." });
      navigate("/hello", { replace: true });
      return;
    }
    push({ title: "Onboarding", message: error.message });
  }, [error, navigate, push, signout]);

  useEffect(() => {
    if (!accessToken || !profile) return;
    if (!needsBillingContextRefresh(profile as Record<string, unknown>)) return;
    fetchCurrentAccount()
      .then((accountProfile) => completeLogin(accessToken, { ...profile, ...accountProfile }))
      .catch(() => {
        // Best-effort refresh; onboarding can proceed without it.
      });
  }, [accessToken, completeLogin, profile]);

  useEffect(() => {
    if (prefillAccount) return;
    const locationState = location.state as OnboardingLocationState | null;
    if (locationState?.prefillAccount) {
      setPrefillAccount(locationState.prefillAccount);
      return;
    }
    if (queryPrefill) setPrefillAccount(queryPrefill);
  }, [location.state, prefillAccount, queryPrefill]);

  useEffect(() => {
    if (!status) return;
    if (requiresBillingBeforeOnboarding(profile as Record<string, unknown> | null)) {
      navigate("/settings/billing", { replace: true });
      return;
    }
    if (status.onboarding_completed) {
      const artistId = status.active_artist_id ?? status.primary_artist?.id;
      if (artistId) setActiveArtistId(artistId);
      // Finishing activation lands in Discover; returning users go Home.
      navigate(justCompletedRef.current ? "/discover" : "/dashboard", { replace: true });
      return;
    }
    // Earlier steps the user has already reached may be revisited (Back);
    // steps beyond their saved progress, and legacy/unknown paths, go to the
    // furthest step reached.
    const target = toActivationStep(status.current_step);
    const shown = stepFromPath(location.pathname);
    if (!shown || STEP_ORDER.indexOf(shown) > STEP_ORDER.indexOf(target)) {
      navigate(`/signup/${target}`, { replace: true });
    }
  }, [status, location.pathname, navigate, profile]);

  const goForward = useCallback(
    (from: ActivationStep, to: ActivationStep) => {
      // Push (not replace) so browser Back returns to the previous step.
      navigate(`/signup/${to}`, { state: { from } satisfies OnboardingLocationState });
    },
    [navigate],
  );

  const goBack = useCallback(
    (from: ActivationStep) => {
      const previous = STEP_ORDER[Math.max(STEP_ORDER.indexOf(from) - 1, 0)];
      const state = location.state as OnboardingLocationState | null;
      if (state?.from === previous) {
        navigate(-1); // the previous history entry is that step
      } else {
        navigate(`/signup/${previous}`);
      }
    },
    [location.state, navigate],
  );

  const handleAccountDone = useCallback(async () => {
    await refreshStatus();
    goForward("account", "artist");
  }, [goForward, refreshStatus]);

  const handleGoalsDone = useCallback(async () => {
    await refreshStatus();
    goForward("goals", "comparables");
  }, [goForward, refreshStatus]);

  const handleArtistCreated = useCallback(
    async (next: OnboardingStatus) => {
      // Signup tokens carry no artists; adopt the reissued token so artist-scoped
      // pages (Discover, Booking) work immediately, and make the new artist active.
      if (next.access_token) {
        completeLogin(next.access_token, (profile ?? {}) as Record<string, unknown>);
      }
      if (next.active_artist_id) setActiveArtistId(next.active_artist_id);
      await refreshStatus();
      goForward("artist", "goals");
    },
    [completeLogin, goForward, profile, refreshStatus],
  );

  const handleFinished = useCallback(async () => {
    justCompletedRef.current = true;
    const artistId = status?.active_artist_id ?? status?.primary_artist?.id;
    if (artistId) setActiveArtistId(artistId);
    navigate("/discover", { replace: true });
  }, [navigate, status]);

  // Furthest step reached (saved progress) vs. the step being shown.
  const reachedStep = toActivationStep(status?.current_step);
  const currentStep = stepFromPath(location.pathname) ?? reachedStep;
  const currentIndex = STEP_ORDER.indexOf(currentStep);
  const reachedIndex = STEP_ORDER.indexOf(reachedStep);
  const defaultEmail = typeof profile?.username === "string" ? (profile.username as string) : "";

  if (loading && !status) {
    return (
      <div className="launch-root flex min-h-screen items-center justify-center text-foreground">
        <div className="relative z-10">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="launch-root min-h-screen text-foreground">
      <div className="launch-hero-bg" aria-hidden="true" />
      <div className="relative z-10 mx-auto flex max-w-2xl flex-col gap-6 px-4 py-8 sm:px-6">
        <header>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Get started</p>
          <h1 className="aa-page-title mt-1 text-3xl font-semibold">
            <span className="aa-gradient-text">Set up ArtistAscend</span>
          </h1>
        </header>
        <ol className="grid grid-cols-4 gap-3" aria-label="Setup progress">
          {STEP_ORDER.map((step, index) => (
            <li key={step} className="flex flex-col gap-1" aria-current={step === currentStep ? "step" : undefined}>
              <span
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold",
                  step === currentStep
                    ? "bg-primary text-primary-foreground"
                    : index < reachedIndex
                      ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-200"
                      : "border border-border text-muted-foreground",
                )}
              >
                {index + 1}
              </span>
              <span className="text-xs text-muted-foreground">{STEP_LABELS[step]}</span>
            </li>
          ))}
        </ol>
        <div className="aa-glass-card rounded-3xl p-5 shadow-lg">
          <Routes>
            <Route path="" element={<Navigate to={`/signup/${reachedStep}`} replace />} />
            <Route
              path="account"
              element={
                <AccountBasicsStep
                  defaultEmail={defaultEmail}
                  prefill={prefillAccount ?? undefined}
                  draft={accountDraft}
                  onDraftChange={setAccountDraft}
                  passwordAlreadySet={reachedIndex > 0}
                  onDone={handleAccountDone}
                />
              }
            />
            <Route
              path="artist"
              element={
                <ArtistBasicsStep
                  draft={artistDraft}
                  savedArtist={status?.primary_artist ?? null}
                  onDraftChange={setArtistDraft}
                  onBack={() => goBack("artist")}
                  onDone={handleArtistCreated}
                />
              }
            />
            <Route
              path="goals"
              element={
                <GoalsStep
                  initialGoals={status?.career_goals ?? []}
                  onBack={() => goBack("goals")}
                  onDone={handleGoalsDone}
                />
              }
            />
            <Route
              path="comparables"
              element={<ComparablesStep onBack={() => goBack("comparables")} onDone={handleFinished} />}
            />
            {/* Legacy step URLs map onto the new journey. */}
            <Route path="organization" element={<Navigate to="/signup/artist" replace />} />
            <Route path="email" element={<Navigate to="/signup/goals" replace />} />
            <Route path="calendar" element={<Navigate to="/signup/goals" replace />} />
            <Route path="completed" element={<Navigate to="/discover" replace />} />
            <Route path="*" element={<Navigate to="account" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
